<template>
    <div class="slice page-legal">
        <div class="slice-content page-legal__columns">
            <Content v-if="cguContent" class="page-legal__content" :readonly="readonly" :content="cguContent" type="cgu" human-id="cgu" />
            <aside class="page-legal__versions">
                <div class="page-legal__versions__title">{{ t('legal.versions.title') }}</div>
                <ul>
                    <li v-for="version in sortedVersions" :key="version" class="page-legal__version">
                        <NuxtLink
                            class="page-legal__link"
                            :class="{'page-legal__link--active': version === currentVersion}"
                            :to="$withLocalePrefix({path: '/legal', query: {v: version}})"
                        >
                            {{ version }}
                        </NuxtLink>
                    </li>
                </ul>
            </aside>
        </div>
    </div>
</template>

<script lang="ts" setup>
    import {ref, watch} from 'vue';
    import {storeToRefs} from 'pinia';
    import {Content} from '#components';
    import {useTranslation, useRoute, useSeoMeta, useAuth, useAsyncData, createError} from '#imports';
    import {Configuration, LegalApiApi, type ContentView} from '@api';

    const {t} = useTranslation();
    const route = useRoute();
    const auth = useAuth();
    const {user, loggedIn} = storeToRefs(auth);
    const readonly = ref(!loggedIn.value || !user.value?.roles.includes('ROLE_TERMS_AND_CONDITIONS'));
    const legalApi = new LegalApiApi(new Configuration({basePath: ''}));
    const cguContent = ref<string>();
    const currentVersion = ref<string>();
    const sortedVersions = ref<string[]>([]);

    function fetchData(): Promise<ContentView> {
        const version = route.query.v as string;
        return legalApi.cgu({version});
    }
    function updateData(data: ContentView) {
        cguContent.value = data.content;
        currentVersion.value = data.currentVersion;
        sortedVersions.value = data.versions.sort((a, b) => b.localeCompare(a));
    }

    const {data, error} = await useAsyncData(fetchData, {deep: true});
    if (!data.value || error.value) {
        throw createError({statusCode: 404, statusMessage: 'Page not found', fatal: true});
    }
    updateData(data.value);

    watch(
        () => route.query,
        async () => {
            const response = await fetchData();
            updateData(response);
            window.scrollTo({top: 0, behavior: 'smooth'});
        },
    );

    useSeoMeta({title: t('legal.title'), robots: 'noindex'});
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .page-legal {
        background: var(--joy-color-neutral-10);

        &__columns {
            text-align: left;

            @include mq.screen_md(min) {
                display: flex;
                align-items: flex-start;
                gap: var(--joy-core-spacing-8);
            }
        }

        &__content {
            flex: 1;
            padding: var(--joy-core-spacing-6);
        }

        &__versions {
            padding-bottom: var(--joy-core-spacing-6);

            @include mq.screen_md(min) {
                flex: none;
                width: 250px;
            }

            &__title {
                padding: var(--joy-core-spacing-6);
                font-weight: var(--joy-font-weight-bold);
                font-size: var(--joy-font-size-primary-600);
            }
        }

        &__version {
            list-style-type: none;
        }

        &__link {
            display: block;
            padding: var(--joy-core-spacing-2) var(--joy-core-spacing-6);
            color: var(--joy-color-text-default);

            &--active,
            &:hover {
                background-color: var(--joy-color-secondary-10);
            }
        }

        &__content,
        &__versions,
        .content_styles {
            border: 1px solid var(--joy-color-neutral-30);
            border-radius: var(--joy-core-radius-3);
            background-color: var(--joy-color-background-default);
        }

        .content_styles {
            padding: var(--joy-core-spacing-6);

            p {
                margin-bottom: var(--joy-core-spacing-3);
            }

            ul {
                padding-left: var(--joy-core-spacing-8);
                margin-bottom: var(--joy-core-spacing-3);
            }
        }
    }
</style>
